import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";

import { FilterAutocomplete } from "../../../../shared/FilterAutocomplete";
import { useCountryListing } from "../../../../api/queries/network/useCountryListing";
import { useFilterState, FilterValueType } from "../../../../shared/FilterAutocomplete/useFilterState";

import { ServiceCategory, ServiceTypeEnum, serviceTypesList } from "../../enum/service.enum";

type ProviderFilters = {
  countryIds: string;
  type: ServiceTypeEnum;
  categories: ServiceCategory;
  rating: number;
  availabilityOption: "online" | "offline";
};

interface FilterConfig<T> {
  key: keyof T;
  options: Array<{ label: string; value: FilterValueType }>;
  width: string;
  placeholder: string;
  isDisabled?: boolean;
}

const Filters = ({ isDataLoading }: { isDataLoading: boolean }) => {
  const { t } = useTranslation("goService", { keyPrefix: "trendingProvidersPage" });
  const { countries, isCountriesDataLoading } = useCountryListing();

  const { filters, updateFilters, hasActiveFilters, clearFilters } = useFilterState<ProviderFilters>({
    fields: {
      countryIds: {
        type: "string",
      },
      type: {
        type: "string",
      },
      categories: {
        type: "string",
      },
      rating: {
        type: "number",
      },
      availabilityOption: {
        type: "string",
      },
    },
  });

  const filterConfigs: FilterConfig<ProviderFilters>[] = [
    {
      key: "countryIds",
      options: countries.map(({ label, value }: { label: string; value: string }) => ({
        label,
        value,
      })),
      width: "200px",
      placeholder: t("country"),
      isDisabled: isDataLoading || isCountriesDataLoading,
    },
    {
      key: "type",
      options: serviceTypesList.map((type: ServiceTypeEnum) => ({
        label: type.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()),
        value: type,
      })),
      width: "180px",
      placeholder: t("type"),
      isDisabled: isDataLoading,
    },
    {
      key: "categories",
      options: Object.values(ServiceCategory).map((category: ServiceCategory) => ({
        label: category.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()),
        value: category,
      })),
      width: "200px",
      placeholder: t("categories"),
      isDisabled: isDataLoading,
    },
    {
      key: "rating",
      options: [
        { label: "1+ Stars", value: 1 },
        { label: "2+ Stars", value: 2 },
        { label: "3+ Stars", value: 3 },
        { label: "4+ Stars", value: 4 },
        { label: "5 Stars", value: 5 },
      ],
      width: "150px",
      placeholder: t("rating"),
      isDisabled: isDataLoading,
    },
    {
      key: "availabilityOption",
      options: [
        { label: "Online", value: "online" },
        { label: "Offline", value: "offline" },
      ],
      width: "150px",
      placeholder: t("availability"),
      isDisabled: isDataLoading,
    },
  ];

  return (
    <Box sx={styles.container}>
      <Box sx={styles.filtersWrapper}>
        {filterConfigs.map((config) => (
          <FilterAutocomplete<ProviderFilters>
            key={config.key.toString()}
            options={config.options}
            value={filters[config.key]}
            filterKey={config.key}
            width={config.width}
            placeholder={config.placeholder}
            disabled={config.isDisabled}
            updateFilters={updateFilters}
          />
        ))}
        {hasActiveFilters && (
          <Button disabled={isDataLoading} variant="text-sm" onClick={clearFilters}>
            {t("clear")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default Filters;

const styles = {
  container: {
    display: "flex",
    gap: "8px",
    width: "100%",
    overflowX: "auto",
    whiteSpace: "nowrap",
    paddingBottom: "8px",
  },
  filtersWrapper: {
    display: "flex",
    gap: "8px",
    width: "fit-content",
  },
};
